import { z } from 'zod';

// The Vorgabe carries black as category V, not as a death finding. Scenarios written
// before that rename still say "dead".
export const triageCategorySchema = z.preprocess(
  (value) => (value === 'dead' ? 'V' : value),
  z.enum(['I', 'II', 'III', 'IV', 'V']),
);
export type TriageCategory = z.infer<typeof triageCategorySchema>;

// Answers along the triage algorithm, in the order the algorithm asks them.
// Source: VA Sichtung von Patienten bei MANV/MANE-Lagen, Bereich 212, v1.0.
export const triageFindingsSchema = z.object({
  ambulatory: z.boolean(), // Patient gehfähig?
  lethalInjury: z.boolean(), // Tödliche Verletzung?
  spontaneousBreathing: z.boolean(), // Spontanatmung vorhanden?
  breathingAfterAirway: z.boolean(), // only asked when spontaneousBreathing is false
  respiratoryRateOver30: z.boolean(), // Atemfrequenz über 30/min?
  spurtingBleeding: z.boolean(), // Spritzende Blutung?
  radialPulseAbsent: z.boolean(), // Fehlender Radialispuls?
  followsCommands: z.boolean(), // Befolgt einfache Befehle?
  survivable: z.boolean(), // Überlebenschance, only used with limited resources
  acuteSurgicalIndication: z.boolean(), // second stage, decided by the LNA
  criticalOnReassessment: z.boolean(), // critical finding at the minor-injury area
});
export type TriageFindings = z.infer<typeof triageFindingsSchema>;

// An unremarkable patient: breathing, perfused, responsive. Ends in II.
export function emptyTriageFindings(): TriageFindings {
  return {
    ambulatory: false,
    lethalInjury: false,
    spontaneousBreathing: true,
    breathingAfterAirway: true,
    respiratoryRateOver30: false,
    spurtingBleeding: false,
    radialPulseAbsent: false,
    followsCommands: true,
    survivable: true,
    acuteSurgicalIndication: false,
    criticalOnReassessment: false,
  };
}

export const TRIAGE_QUESTIONS = {
  ambulatory: 'Patient gehfähig?',
  lethalInjury: 'Tödliche Verletzung?',
  spontaneousBreathing: 'Spontanatmung vorhanden?',
  breathingAfterAirway: 'Spontanatmung nach Freimachen der Atemwege?',
  respiratoryRateOver30: 'Atemfrequenz über 30/min?',
  spurtingBleeding: 'Spritzende Blutung?',
  radialPulseAbsent: 'Fehlender Radialispuls?',
  followsCommands: 'Befolgt einfache Befehle?',
  survivable: 'Überlebenschance?',
  acuteSurgicalIndication: 'Akute OP-Indikation?',
  criticalOnReassessment: 'Kritischer Befund bei der Nachuntersuchung?',
} as const satisfies Record<keyof TriageFindings, string>;

const yesNo = (value: boolean) => (value ? 'Ja' : 'Nein');

// The algorithm, step by step. `reason` names the step that decided the category.
type Outcome = { category: TriageCategory; reason: string };

function walk(findings: TriageFindings): Outcome {
  if (findings.ambulatory) return { category: 'III', reason: 'gehfähig' };
  if (findings.lethalInjury) {
    return { category: 'V', reason: 'tödliche Verletzung, Befundbestätigung durch den Arzt' };
  }
  if (!findings.spontaneousBreathing) {
    return findings.breathingAfterAirway
      ? { category: 'I', reason: 'Spontanatmung erst nach Freimachen der Atemwege' }
      : {
          category: 'V',
          reason: 'keine Spontanatmung, auch nicht nach Freimachen der Atemwege',
        };
  }
  if (findings.respiratoryRateOver30) return { category: 'I', reason: 'Atemfrequenz über 30/min' };
  // Spurting bleeding triggers haemostasis but does not decide the category.
  if (findings.radialPulseAbsent) return { category: 'I', reason: 'fehlender Radialispuls' };
  if (!findings.followsCommands)
    return { category: 'I', reason: 'befolgt keine einfachen Befehle' };
  return { category: 'II', reason: 'keines der Kriterien für I erfüllt' };
}

export function preTriageCategory(
  findings: TriageFindings,
  limitedResources: boolean,
): TriageCategory {
  return explainCategory(findings, limitedResources).category;
}

export function explainCategory(findings: TriageFindings, limitedResources: boolean): Outcome {
  const outcome = walk(findings);
  const treatable = outcome.category === 'I' || outcome.category === 'II';
  if (limitedResources && treatable && !findings.survivable) {
    return { category: 'IV', reason: 'limitierte Ressourcen, keine Überlebenschance' };
  }
  return outcome;
}

export type TriageQuestionRow = { field: keyof TriageFindings; question: string; answer: string };

// The questions the algorithm actually asks this patient, in order, with the answers from
// the scenario. Steps the algorithm skips are left out, so the instructor only reads back
// what the candidate can ask for. `field` names which finding the row came from: the editor's
// reachability (src/ui/editor/TriageFields.tsx) is derived from this list, so this is the
// single place that encodes which questions the algorithm reaches.
export function triageQuestions(
  findings: TriageFindings,
  limitedResources: boolean,
): TriageQuestionRow[] {
  const rows: TriageQuestionRow[] = [];
  const ask = (key: keyof TriageFindings) => {
    rows.push({ field: key, question: TRIAGE_QUESTIONS[key], answer: yesNo(findings[key]) });
  };

  ask('ambulatory');
  if (findings.ambulatory) return rows;

  ask('lethalInjury');
  if (findings.lethalInjury) return rows;

  ask('spontaneousBreathing');
  if (!findings.spontaneousBreathing) {
    ask('breathingAfterAirway');
    // breathingAfterAirway true reaches I (treatable); false reaches V, which the
    // limited-resources branch never touches, so the question stays unasked there.
    if (findings.breathingAfterAirway && limitedResources) ask('survivable');
    return rows;
  }

  ask('respiratoryRateOver30');
  if (findings.respiratoryRateOver30) {
    if (limitedResources) ask('survivable');
    return rows;
  }

  ask('spurtingBleeding');
  ask('radialPulseAbsent');
  if (findings.radialPulseAbsent) {
    if (limitedResources) ask('survivable');
    return rows;
  }

  ask('followsCommands');
  if (limitedResources) ask('survivable');
  return rows;
}
